import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Question } from '@foundry/shared';
import { ApiError, apiFetch } from './client.js';
import type {
  AuditEntry,
  CallStatus,
  DashboardData,
  DeveloperRef,
  HoursStatus,
  NewStory,
  Project,
  ProjectAttention,
  Specification,
  Story,
  StoryEdit,
} from './types.js';

export function useProjects() {
  return useQuery({
    queryKey: ['projects'],
    queryFn: () => apiFetch<{ projects: Project[] }>('/projects').then((r) => r.projects),
  });
}

export function useCreateProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (name: string) =>
      apiFetch<{ project: Project }>('/projects', { method: 'POST', body: { name } }).then(
        (r) => r.project,
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['projects'] }),
  });
}

function useProjectAction(action: 'pause' | 'resume') {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<{ project: Project }>(`/projects/${id}/${action}`, { method: 'POST' }).then(
        (r) => r.project,
      ),
    onSuccess: (project) => {
      qc.setQueryData(['project', project.id], project);
      void qc.invalidateQueries({ queryKey: ['projects'] });
    },
  });
}

export const usePauseProject = () => useProjectAction('pause');
export const useResumeProject = () => useProjectAction('resume');

export function useDeleteProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiFetch<{ ok: true }>(`/projects/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['projects'] }),
  });
}

/** Founder asks for a source-code download; lands a message in the project feed. */
export function useRequestSourceDownload(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => apiFetch<{ ok: true }>(`/projects/${id}/source-request`, { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['messages', id] }),
  });
}

export function useProject(id: string) {
  return useQuery({
    queryKey: ['project', id],
    queryFn: () => apiFetch<{ project: Project }>(`/projects/${id}`).then((r) => r.project),
    // While the model is generating a spec or stories, poll so the view updates
    // to the finished result on its own (even after navigating away and back).
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status === 'generating_spec' || status === 'generating_stories' ? 2500 : false;
    },
  });
}

export function useDashboard(id: string) {
  return useQuery({
    queryKey: ['dashboard', id],
    queryFn: () =>
      apiFetch<{ dashboard: DashboardData }>(`/projects/${id}/dashboard`).then((r) => r.dashboard),
  });
}

export function useCallStatus(id: string) {
  return useQuery({
    queryKey: ['call-status', id],
    queryFn: () =>
      apiFetch<{ status: CallStatus }>(`/projects/${id}/call-status`).then((r) => r.status),
  });
}

export function useHoursStatus(id: string) {
  return useQuery({
    queryKey: ['hours-status', id],
    queryFn: () =>
      apiFetch<{ status: HoursStatus }>(`/projects/${id}/hours-status`).then((r) => r.status),
  });
}

export function useProjectAttention(id: string) {
  return useQuery({
    queryKey: ['attention', id],
    queryFn: () =>
      apiFetch<{ attention: ProjectAttention }>(`/projects/${id}/attention`).then(
        (r) => r.attention,
      ),
  });
}

export function useMarkMessagesRead(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => apiFetch<{ ok: true }>(`/projects/${id}/messages/read`, { method: 'POST' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['attention', id] });
      // Refetch the feed so its `lastReadAt` advances and the "New" flags clear.
      void qc.invalidateQueries({ queryKey: ['messages', id] });
    },
  });
}

export function useProjectAudit(id: string) {
  return useQuery({
    queryKey: ['audit', id],
    queryFn: () =>
      apiFetch<{ entries: AuditEntry[] }>(`/projects/${id}/audit`).then((r) => r.entries),
  });
}

export function useQuestionnaire(id: string) {
  return useQuery({
    queryKey: ['questionnaire', id],
    queryFn: () =>
      apiFetch<{ questions: Question[]; answers: Record<string, string> }>(
        `/projects/${id}/questionnaire`,
      ),
  });
}

export function useSaveAnswer(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { key: string; value: string }) =>
      apiFetch<{ ok: true }>(`/projects/${id}/questionnaire/${input.key}`, {
        method: 'PUT',
        body: { value: input.value },
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['questionnaire', id] }),
  });
}

/** The latest specification, or `null` when none has been generated yet. */
export function useSpecification(id: string) {
  return useQuery({
    queryKey: ['specification', id],
    queryFn: async (): Promise<Specification | null> => {
      try {
        return (await apiFetch<{ specification: Specification }>(`/projects/${id}/specification`))
          .specification;
      } catch (error) {
        if (error instanceof ApiError && error.status === 404) return null;
        throw error;
      }
    },
  });
}

export function useGenerateSpec(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch<{ specification: Specification }>(`/projects/${id}/generate-spec`, {
        method: 'POST',
      }).then((r) => r.specification),
    onSuccess: (specification) => {
      qc.setQueryData(['specification', id], specification);
      void qc.invalidateQueries({ queryKey: ['project', id] });
    },
  });
}

export function useSetSourceDocument(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (content: string) =>
      apiFetch<{ project: Project }>(`/projects/${id}/source-document`, {
        method: 'PUT',
        body: { content },
      }).then((r) => r.project),
    onSuccess: (project) => {
      qc.setQueryData(['project', id], project);
      void qc.invalidateQueries({ queryKey: ['project', id] });
    },
  });
}

export function useSpecDecision(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { decision: 'approve' | 'request_changes' | 'share'; note?: string }) =>
      apiFetch<{ specification: Specification }>(`/projects/${id}/specification/decision`, {
        method: 'POST',
        body: { decision: input.decision, ...(input.note ? { note: input.note } : {}) },
      }).then((r) => r.specification),
    onSuccess: (specification) => {
      qc.setQueryData(['specification', id], specification);
      void qc.invalidateQueries({ queryKey: ['project', id] });
    },
  });
}

export function useStoryDecision(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      storyId: string;
      decision: 'ready' | 'approve' | 'unapprove' | 'accept' | 'reopen';
    }) =>
      apiFetch<{ story: Story }>(`/projects/${id}/stories/${input.storyId}/decision`, {
        method: 'POST',
        body: { decision: input.decision },
      }).then((r) => r.story),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['stories', id] }),
  });
}

export function useUpdateStory(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { storyId: string; edit: StoryEdit }) =>
      apiFetch<{ story: Story }>(`/projects/${id}/stories/${input.storyId}`, {
        method: 'PATCH',
        body: input.edit,
      }).then((r) => r.story),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['stories', id] }),
  });
}

export function useCreateStory(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (story: NewStory) =>
      apiFetch<{ story: Story }>(`/projects/${id}/stories`, {
        method: 'POST',
        body: story,
      }).then((r) => r.story),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['stories', id] }),
  });
}

export function useDeleteStory(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (storyId: string) =>
      apiFetch<{ ok: true }>(`/projects/${id}/stories/${storyId}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['stories', id] }),
  });
}

export function useReorderStories(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (order: string[]) =>
      apiFetch<{ stories: Story[] }>(`/projects/${id}/stories/reorder`, {
        method: 'POST',
        body: { order },
      }).then((r) => r.stories),
    onSuccess: (stories) => qc.setQueryData(['stories', id], stories),
  });
}

export function useStories(id: string) {
  return useQuery({
    queryKey: ['stories', id],
    queryFn: () => apiFetch<{ stories: Story[] }>(`/projects/${id}/stories`).then((r) => r.stories),
  });
}

/** Staff-only: reset all stories to draft and ensure a Hello-World story. */
export function useResetStories(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch<{ stories: Story[] }>(`/projects/${id}/stories/reset`, { method: 'POST' }).then(
        (r) => r.stories,
      ),
    onSuccess: (stories) => {
      qc.setQueryData(['stories', id], stories);
      void qc.invalidateQueries({ queryKey: ['project', id] });
    },
  });
}

/** Staff-only: wipe the project's repository (reset main to a fresh README). */
export function useWipeRepo(id: string) {
  return useMutation({
    mutationFn: () => apiFetch<{ ok: true }>(`/projects/${id}/repository/wipe`, { method: 'POST' }),
  });
}

export function useGenerateStories(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch<{ stories: Story[] }>(`/projects/${id}/generate-stories`, { method: 'POST' }).then(
        (r) => r.stories,
      ),
    onSuccess: (stories) => qc.setQueryData(['stories', id], stories),
  });
}

export function useAssignments(id: string) {
  return useQuery({
    queryKey: ['assignments', id],
    queryFn: () =>
      apiFetch<{ assignments: DeveloperRef[]; developers: DeveloperRef[] }>(
        `/projects/${id}/assignments`,
      ),
  });
}

export function useAssignDeveloper(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) =>
      apiFetch<{ assignments: DeveloperRef[] }>(`/projects/${id}/assignments`, {
        method: 'POST',
        body: { userId },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['assignments', id] });
      void qc.invalidateQueries({ queryKey: ['projects'] });
    },
  });
}

export function useUnassignDeveloper(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) =>
      apiFetch<{ assignments: DeveloperRef[] }>(`/projects/${id}/assignments/${userId}`, {
        method: 'DELETE',
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['assignments', id] });
      void qc.invalidateQueries({ queryKey: ['projects'] });
    },
  });
}

export function useSetRepository(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (fullName: string) =>
      apiFetch<{ project: Project }>(`/projects/${id}/repository`, {
        method: 'PUT',
        body: { fullName },
      }).then((r) => r.project),
    onSuccess: (project) => {
      qc.setQueryData(['project', id], project);
      // The deployment status carries `hasRepository`; refresh it so the Live
      // testing panel stops asking to set a repo that was just set.
      void qc.invalidateQueries({ queryKey: ['deployment', id] });
    },
  });
}
